// views/Dashboard/PainelExecutivo/sections/equipes/useEquipesData.ts
//
// Dados da área "Equipes" do Painel Executivo (#6198): quem está em cada
// unidade (profiles.unidade), os indicadores de cada pessoa (RPC
// `bi_eficiencia_equipes`, só responde para adm) e as emissões de boleto que
// dão o prazo do Faturamento.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../../../../src/lib/supabase';
import type { EmissaoFaturamento } from '../../../../../src/lib/financeiro/metricasFaturamento';
import {
  agruparLinhas,
  ehUnidade,
  montarPainelEquipes,
  valoresDeEmissao,
  type LinhaIndicador,
  type PessoaBi,
  type UnidadeBi,
} from '../../../../../src/lib/dashboard/eficienciaEquipes';
import { buscarEmissoes } from '../../../DashboardFaturamento/hooks/useDashboardFaturamentoData';
import type { JanelaEmissao } from '../../../DashboardFaturamento/lib/periodoEmissao';

const SEM_MIGRATION =
  'O BI ainda não está instalado no banco: falta aplicar a migration 20261015170000_bi_eficiencia_equipes.sql.';

/** A janela termina em 23:59:59.999 do último dia; a RPC quer o fim exclusivo. */
const fimExclusivo = (janela: JanelaEmissao | null) => (janela ? new Date(janela.fim.getTime() + 1) : null);

async function buscarPessoas(): Promise<PessoaBi[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, full_name, departamento, unidade')
    .eq('status', 'active')
    .not('unidade', 'is', null);

  if (error) {
    // Coluna inexistente = migration não aplicada (42703).
    if (error.code === '42703') throw new Error(SEM_MIGRATION);
    throw error;
  }

  return (data || [])
    .filter((p: any) => ehUnidade(p.unidade))
    .map((p: any) => ({
      id: p.id,
      nome: p.full_name || 'Sem nome',
      departamento: p.departamento ?? null,
      unidade: p.unidade,
    }));
}

async function buscarIndicadores(janela: JanelaEmissao | null): Promise<LinhaIndicador[]> {
  const fim = fimExclusivo(janela);
  const { data, error } = await supabase.rpc('bi_eficiencia_equipes', {
    p_inicio: janela ? janela.inicio.toISOString() : null,
    p_fim: fim ? fim.toISOString() : null,
  });

  if (error) {
    // Função inexistente no cache do PostgREST = migration não aplicada.
    if (error.code === 'PGRST202') throw new Error(SEM_MIGRATION);
    throw error;
  }
  return (data || []) as LinhaIndicador[];
}

/** `janela` precisa vir memorizada: cada referência nova recarrega tudo. */
export function useEquipesData(janela: JanelaEmissao | null) {
  const [pessoas, setPessoas] = useState<PessoaBi[]>([]);
  const [linhas, setLinhas] = useState<LinhaIndicador[]>([]);
  const [emissoes, setEmissoes] = useState<EmissaoFaturamento[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [erroPrazo, setErroPrazo] = useState<string | null>(null);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date | null>(null);

  // Trocar de período rápido dispara cargas em paralelo; só a mais nova escreve.
  const carga = useRef(0);

  const carregar = useCallback(async () => {
    const minha = ++carga.current;
    setLoading(true);
    setErro(null);
    setErroPrazo(null);
    setEmissoes(null);

    // As emissões vão à parte: é a carga mais pesada da tela, e só as colunas
    // de prazo do Faturamento dependem dela — o resto não fica esperando.
    buscarEmissoes(janela?.inicio ?? null)
      .then((lista) => { if (carga.current === minha) setEmissoes(lista); })
      .catch((e: any) => {
        console.error('Erro ao carregar as emissões do BI de equipes:', e);
        if (carga.current !== minha) return;
        setErroPrazo('Não foi possível carregar o prazo de emissão do Faturamento.');
        setEmissoes([]);
      });

    try {
      const [p, l] = await Promise.all([buscarPessoas(), buscarIndicadores(janela)]);
      if (carga.current !== minha) return;
      setPessoas(p);
      setLinhas(l);
      setUltimaAtualizacao(new Date());
    } catch (e: any) {
      console.error('Erro ao carregar o BI de equipes:', e);
      if (carga.current === minha) setErro(e?.message || 'Erro ao carregar os indicadores das equipes.');
    } finally {
      if (carga.current === minha) setLoading(false);
    }
  }, [janela]);

  useEffect(() => { carregar(); }, [carregar]);

  const unidades: UnidadeBi[] = useMemo(() => {
    const porNome = emissoes
      ? valoresDeEmissao(emissoes, janela?.inicio ?? null, fimExclusivo(janela))
      : new Map();
    return montarPainelEquipes(pessoas, agruparLinhas(linhas), porNome);
  }, [pessoas, linhas, emissoes, janela]);

  return {
    unidades,
    loading,
    erro,
    carregandoPrazo: emissoes === null,
    erroPrazo,
    ultimaAtualizacao,
    recarregar: carregar,
  };
}
